// ==========================================================================
// ContextMenu + Chatbot Plugin
// ==========================================================================

const DEFAULT_BASE_URL = 'https://scms-backend-263017977289.asia-northeast3.run.app/api/v1'
const WELCOME_TEXT = '안녕하세요! StarPlayer 고객지원 챗봇입니다. 어떤 도움이 필요하신가요?'
const INITIAL_SUGGESTIONS = ['차단 확인', '해제 요청', 'FAQ', '네트워크 속도 측정']

class ApiError extends Error {
  constructor (status, body) {
    super(body?.message || `API error: ${status}`)
    this.status = status
    this.body = body
  }
}

class ContextMenu {
  constructor (player) {
    this.player = player

    const cfg = (player.config.contextMenu) || {}
    if (cfg.enabled === false) return

    this._menuEl = null

    this._chatContainer = null
    this._chatVisible = false
    this._msgContainer = null
    this._suggestionsEl = null
    this._loadingEl = null
    this._inputEl = null
    this._sendBtn = null

    this._messages = []
    this._sessionId = undefined
    this._suggestions = []
    this._isLoading = false

    const container = this.player.elements.container
    if (container) {
      container.addEventListener('contextmenu', this._onContextMenu, true)
    }
    // 에러 오버레이(.plyr__message)가 wrapper 내에 표시될 때도 우클릭이 동작하도록
    const wrapper = this.player.elements.wrapper
    if (wrapper) {
      wrapper.addEventListener('contextmenu', this._onContextMenu, true)
    }
    document.addEventListener('click', this._onDocumentClick, true)
    document.addEventListener('keydown', this._onKeyDown)
  }

  // ────────────────────────────────
  //  Context Menu
  // ────────────────────────────────

  _onContextMenu = (e) => {
    e.preventDefault()
    e.stopPropagation()
    this._showMenu(e.clientX, e.clientY)
  }

  _onDocumentClick = (e) => {
    if (!this._menuEl) return
    if (this._menuEl.contains(e.target)) return
    this._hideMenu()
  }

  _onKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (this._menuEl) {
        this._hideMenu()
      } else if (this._chatVisible) {
        this._closeChatPanel()
      }
    }
  }

  _showMenu (x, y) {
    this._hideMenu()

    const root = this.player.elements.container
    if (!root) return

    const menu = document.createElement('div')
    menu.className = 'starplayer-ctx-menu'

    this._buildMenuItems().forEach(item => {
      const el = document.createElement('div')
      el.className = 'starplayer-ctx-menu-item' + (item.disabled ? ' is-disabled' : '')
      el.innerHTML =
        `<span class="starplayer-ctx-menu-icon">${item.icon}</span>` +
        `<span class="starplayer-ctx-menu-label">${item.label}</span>`
      if (item.onClick && !item.disabled) {
        el.addEventListener('click', (e) => {
          e.stopPropagation()
          this._hideMenu()
          item.onClick()
        })
      }
      menu.appendChild(el)
    })

    root.appendChild(menu)

    const rootRect = root.getBoundingClientRect()
    const menuRect = menu.getBoundingClientRect()
    let left = x - rootRect.left
    let top = y - rootRect.top

    if (left + menuRect.width > rootRect.width) {
      left = Math.max(0, left - menuRect.width)
    }
    if (top + menuRect.height > rootRect.height) {
      top = Math.max(0, top - menuRect.height)
    }

    menu.style.left = left + 'px'
    menu.style.top = top + 'px'

    this._menuEl = menu
  }

  _hideMenu () {
    if (this._menuEl && this._menuEl.parentNode) {
      this._menuEl.parentNode.removeChild(this._menuEl)
    }
    this._menuEl = null
  }

  _buildMenuItems () {
    const items = []
    const chatbotCfg = this.player.config.contextMenu && this.player.config.contextMenu.chatbot

    if (chatbotCfg && chatbotCfg.enabled) {
      items.push({
        label: '고객지원',
        icon: this._iconChat(),
        onClick: () => this._toggleChatbot()
      })
    }

    // 통신 방식 표시
    const ws = this.player.stars && this.player.stars.ws
    const transport = ws && ws.constructor && ws.constructor.name === 'NativeChannel'
      ? 'Native Messaging'
      : ws ? 'WebSocket' : 'N/A'
    items.push({
      label: transport,
      icon: this._iconConnection(),
      disabled: true
    })

    items.push({
      label: 'StarPlayer ' + (this.player.config.version || ''),
      icon: this._iconStar(),
      disabled: true
    })

    return items
  }

  // ────────────────────────────────
  //  Chat Panel (in-player)
  // ────────────────────────────────

  _toggleChatbot () {
    if (this._chatContainer) {
      this._chatVisible ? this._closeChatPanel() : this._openChatPanel()
      return
    }
    this._createChatPanel()
  }

  _createChatPanel () {
    const root = this.player.elements.container
    if (!root) return

    // Container
    const container = document.createElement('div')
    container.className = 'sp-chat-container'

    // Backdrop
    const backdrop = document.createElement('div')
    backdrop.className = 'sp-chat-backdrop'
    backdrop.addEventListener('click', () => this._closeChatPanel())

    // Panel
    const panel = document.createElement('div')
    panel.className = 'sp-chat-panel'

    // ── Header
    const header = document.createElement('div')
    header.className = 'sp-chat-header'
    header.innerHTML =
      '<div class="sp-chat-title">' + this._iconChat() + '<span>고객지원</span></div>'

    const closeBtn = document.createElement('button')
    closeBtn.className = 'sp-chat-close'
    closeBtn.type = 'button'
    closeBtn.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
    closeBtn.addEventListener('click', () => this._closeChatPanel())
    header.appendChild(closeBtn)

    // ── Body (messages + loading)
    const body = document.createElement('div')
    body.className = 'sp-chat-body'

    const msgContainer = document.createElement('div')
    msgContainer.className = 'sp-chat-messages'
    this._msgContainer = msgContainer

    const loadingEl = document.createElement('div')
    loadingEl.className = 'sp-chat-loading'
    loadingEl.style.display = 'none'
    loadingEl.innerHTML = '<span></span><span></span><span></span>'
    this._loadingEl = loadingEl

    body.appendChild(msgContainer)
    body.appendChild(loadingEl)

    // ── Suggestions
    const suggestionsEl = document.createElement('div')
    suggestionsEl.className = 'sp-chat-suggestions'
    this._suggestionsEl = suggestionsEl

    // ── Input area
    const inputArea = document.createElement('div')
    inputArea.className = 'sp-chat-input-area'

    const input = document.createElement('input')
    input.className = 'sp-chat-input'
    input.type = 'text'
    input.placeholder = '메시지를 입력하세요...'
    input.maxLength = 500
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.stopPropagation()
        this._handleSend()
      }
    })
    this._inputEl = input

    const sendBtn = document.createElement('button')
    sendBtn.className = 'sp-chat-send'
    sendBtn.type = 'button'
    sendBtn.textContent = '전송'
    sendBtn.addEventListener('click', () => this._handleSend())
    this._sendBtn = sendBtn

    inputArea.appendChild(input)
    inputArea.appendChild(sendBtn)

    // ── Assemble
    panel.appendChild(header)
    panel.appendChild(body)
    panel.appendChild(suggestionsEl)
    panel.appendChild(inputArea)

    container.appendChild(backdrop)
    container.appendChild(panel)
    root.appendChild(container)

    this._chatContainer = container

    // Show welcome
    this._appendMessageEl('bot', WELCOME_TEXT)
    this._updateSuggestions(INITIAL_SUGGESTIONS)

    // Animate in
    requestAnimationFrame(() => {
      container.classList.add('is-open')
      setTimeout(() => input.focus(), 300)
    })
    this._chatVisible = true
  }

  _openChatPanel () {
    if (!this._chatContainer) return
    this._chatContainer.classList.add('is-open')
    this._chatVisible = true
    setTimeout(() => {
      if (this._inputEl) this._inputEl.focus()
    }, 300)
  }

  _closeChatPanel () {
    if (!this._chatContainer) return
    this._chatContainer.classList.remove('is-open')
    this._chatVisible = false
  }

  // ────────────────────────────────
  //  Chat Rendering
  // ────────────────────────────────

  _appendMessageEl (role, text) {
    if (!this._msgContainer) return
    const el = document.createElement('div')
    el.className = `sp-chat-msg sp-chat-msg--${role}`
    el.innerHTML = this._linkify(text)
    this._msgContainer.appendChild(el)
    this._scrollToBottom()
  }

  _updateSuggestions (actions) {
    this._suggestions = actions || []
    if (!this._suggestionsEl) return

    this._suggestionsEl.innerHTML = ''
    if (this._suggestions.length === 0) {
      this._suggestionsEl.style.display = 'none'
      return
    }
    this._suggestionsEl.style.display = ''

    for (const action of this._suggestions) {
      const btn = document.createElement('button')
      btn.className = 'sp-chat-suggestion'
      btn.textContent = action
      btn.addEventListener('click', () => {
        if (action === '네트워크 속도 측정') {
          this._runSpeedTest()
        } else {
          this._sendMessage(action)
        }
      })
      this._suggestionsEl.appendChild(btn)
    }
  }

  _setLoading (on) {
    this._isLoading = on
    if (this._loadingEl) this._loadingEl.style.display = on ? 'flex' : 'none'
    if (this._sendBtn) this._sendBtn.disabled = on
    if (this._inputEl) this._inputEl.disabled = on
    if (on) this._scrollToBottom()
  }

  _scrollToBottom () {
    if (!this._msgContainer) return
    requestAnimationFrame(() => {
      const body = this._msgContainer.parentNode
      if (body) body.scrollTop = body.scrollHeight
    })
  }

  _linkify (text) {
    const escaped = String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
    return escaped
      .replace(/\n/g, '<br>')
      .replace(
        /(https?:\/\/[^\s<]+)/g,
        '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
      )
  }

  // ────────────────────────────────
  //  Chat Logic
  // ────────────────────────────────

  _handleSend () {
    if (!this._inputEl) return
    const text = this._inputEl.value.trim()
    if (!text || this._isLoading) return
    this._inputEl.value = ''
    this._sendMessage(text)
  }

  async _sendMessage (text) {
    this._appendMessageEl('user', text)
    this._updateSuggestions([])
    this._setLoading(true)

    const params = this._resolveChatbotParams()

    try {
      const res = await this._apiChat(
        params.baseUrl, params.apiKey,
        params.userId, text, this._sessionId
      )
      this._sessionId = res.session_id
      this._appendMessageEl('bot', res.reply)
      this._updateSuggestions(res.suggested_actions || [])
    } catch (err) {
      this._appendMessageEl('bot', this._formatError(err))
    } finally {
      this._setLoading(false)
      if (this._inputEl) this._inputEl.focus()
    }
  }

  async _runSpeedTest () {
    if (this._isLoading) return
    this._updateSuggestions([])
    this._appendMessageEl('system', '네트워크 속도 측정 중...')
    this._setLoading(true)

    const params = this._resolveChatbotParams()

    try {
      const pings = []
      for (let i = 0; i < 3; i++) {
        try { pings.push(await this._apiPing(params.baseUrl, params.apiKey)) } catch { /* skip */ }
      }
      const avgPing = pings.length > 0
        ? Math.round(pings.reduce((a, b) => a + b, 0) / pings.length)
        : null

      let downloadMbps = null
      try {
        const result = await this._apiSpeedTest(params.baseUrl, params.apiKey, 1)
        downloadMbps = result.mbps
      } catch { /* skip */ }

      if (avgPing !== null && downloadMbps !== null) {
        let verdict = ''
        if (avgPing > 200) verdict = '⚠️ 지연시간이 높습니다.'
        else if (downloadMbps < 5) verdict = '⚠️ 다운로드 속도가 느립니다.'
        else verdict = '✅ 네트워크 상태가 양호합니다.'

        this._appendMessageEl('system',
          `측정 결과: 지연시간 ${avgPing}ms / 다운로드 ${downloadMbps}Mbps\n${verdict}`
        )
        this._setLoading(false)

        await this._sendMessage(`[속도측정결과] Ping: ${avgPing}ms, Download: ${downloadMbps}Mbps`)
      } else {
        this._appendMessageEl('system', '속도 측정에 실패했습니다. 다시 시도해주세요.')
        this._setLoading(false)
      }
    } catch (err) {
      const msg = (err instanceof ApiError && err.status === 403)
        ? this._formatError(err)
        : '속도 측정 중 오류가 발생했습니다.'
      this._appendMessageEl('system', msg)
      this._setLoading(false)
    }
  }

  // ────────────────────────────────
  //  API Calls
  // ────────────────────────────────

  async _apiChat (baseUrl, apiKey, userId, message, sessionId) {
    const res = await fetch(`${baseUrl}/cs-api/chat`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify({
        user_id: userId,
        message: message,
        session_id: sessionId
      })
    })
    if (!res.ok) {
      const body = await res.json().catch(() => null)
      throw new ApiError(res.status, body)
    }
    return res.json()
  }

  async _apiPing (baseUrl, apiKey) {
    const start = performance.now()
    const res = await fetch(`${baseUrl}/cs-api/ping`, {
      mode: 'cors',
      headers: { 'X-API-Key': apiKey },
      cache: 'no-store'
    })
    if (!res.ok) {
      const body = await res.json().catch(() => null)
      throw new ApiError(res.status, body)
    }
    return performance.now() - start
  }

  async _apiSpeedTest (baseUrl, apiKey, sizeMB) {
    const start = performance.now()
    const res = await fetch(`${baseUrl}/cs-api/speed-test?size=${sizeMB}`, {
      mode: 'cors',
      headers: { 'X-API-Key': apiKey },
      cache: 'no-store'
    })
    if (!res.ok) {
      const body = await res.json().catch(() => null)
      throw new ApiError(res.status, body)
    }
    const blob = await res.blob()
    const elapsed = (performance.now() - start) / 1000
    const mbps = parseFloat(((blob.size * 8) / elapsed / 1_000_000).toFixed(1))
    return { mbps, elapsed }
  }

  // ────────────────────────────────
  //  Config
  // ────────────────────────────────

  _resolveChatbotParams () {
    const cfg = (this.player.config.contextMenu && this.player.config.contextMenu.chatbot) || {}
    const sp = this.player.config.starplayer || {}

    return {
      apiKey:  cfg.apiKey  || sp.apiKey || '',
      userId:  cfg.userId  || sp.userId || '',
      baseUrl: cfg.baseUrl || sp.apiBase || DEFAULT_BASE_URL
    }
  }

  _formatError (err) {
    if (err instanceof ApiError) {
      const body = err.body
      if (err.status === 403 && body?.error === 'PLAN_UPGRADE_REQUIRED') {
        return `현재 플랜(${body.currentPlan})에서는 CS 챗봇 기능을 사용할 수 없습니다. '${body.requiredPlan}' 이상 플랜이 필요합니다.`
      }
      if (err.status === 401) {
        return 'API 인증에 실패했습니다. API Key를 확인해주세요.'
      }
      if (body?.message) {
        return body.message
      }
      return `서버 오류가 발생했습니다. (${err.status})`
    }
    if (err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError')) {
      return '네트워크 연결을 확인해주세요.'
    }
    return '죄송합니다. 일시적인 오류가 발생했습니다. 다시 시도해주세요.'
  }

  // ────────────────────────────────
  //  Icons
  // ────────────────────────────────

  _iconChat () {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
           'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
           '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>'
  }

  _iconConnection () {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
           'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
           '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>' +
           '<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>'
  }

  _iconStar () {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
           'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
           '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>'
  }

  // ────────────────────────────────
  //  Lifecycle
  // ────────────────────────────────

  destroy () {
    const container = this.player.elements.container
    if (container) {
      container.removeEventListener('contextmenu', this._onContextMenu, true)
    }
    const wrapper = this.player.elements.wrapper
    if (wrapper) {
      wrapper.removeEventListener('contextmenu', this._onContextMenu, true)
    }
    document.removeEventListener('click', this._onDocumentClick, true)
    document.removeEventListener('keydown', this._onKeyDown)
    this._hideMenu()
    if (this._chatContainer && this._chatContainer.parentNode) {
      this._chatContainer.parentNode.removeChild(this._chatContainer)
    }
    this._chatContainer = null
    this._msgContainer = null
    this._suggestionsEl = null
    this._loadingEl = null
    this._inputEl = null
    this._sendBtn = null
  }
}

export default ContextMenu
